import { useEffect, useState } from 'react'
import ProgressIndicator from '../components/ProgressIndicator'
import { api, ApiResponse, OAuthCompleteResponse, SyncTriggerResponse } from '../utils/api'
import { parseCallbackParams, cleanUrl } from '../utils/urlParams'

type Step = 'oauth' | 'linking' | 'sync' | 'complete'

interface StepState {
  label: string
  status: 'pending' | 'active' | 'completed'
}

const Callback = () => {
  const [currentStep, setCurrentStep] = useState<Step>('oauth')
  const [error, setError] = useState<string>('')
  const [isComplete, setIsComplete] = useState<boolean>(false)
  const [companyName, setCompanyName] = useState<string>('')
  const [connectionDetails, setConnectionDetails] = useState<{
    connectionId?: string
    sourceId?: string
    destinationId?: string
  }>({})

  const steps: StepState[] = [
    {
      label: 'Completing OAuth authentication',
      status: currentStep === 'oauth' ? 'active' : 
             ['linking', 'sync', 'complete'].includes(currentStep) ? 'completed' : 'pending'
    },
    {
      label: 'Linking your QuickBooks account',
      status: currentStep === 'linking' ? 'active' :
             ['sync', 'complete'].includes(currentStep) ? 'completed' : 'pending'
    },
    {
      label: 'Starting initial data sync',
      status: currentStep === 'sync' ? 'active' :
             currentStep === 'complete' ? 'completed' : 'pending'
    },
    {
      label: 'Setup complete!',
      status: currentStep === 'complete' ? 'completed' : 'pending'
    }
  ]

  useEffect(() => {
    const params = parseCallbackParams()
    
    if (params.error) {
      setError(params.error_description || params.error)
      return
    }

    if (!params.code || !params.state) {
      setError('Missing required OAuth parameters')
      return
    }

    // Clean URL for security
    cleanUrl()

    completeFlow(params.code, params.state, params.realmId)
  }, [])

  const completeFlow = async (code: string, state: string, realmId?: string) => {
    try {
      // Step 1: Complete OAuth
      setCurrentStep('oauth')
      await sleep(800) // Small delay for UX

      const oauthResponse: ApiResponse<OAuthCompleteResponse> = await api.completeOAuth({
        code,
        state,
        realmId
      })

      if (!oauthResponse.success || !oauthResponse.data) {
        throw new Error(oauthResponse.message || 'Failed to complete OAuth')
      }

      const { connectionId, sourceId, destinationId, companyName: company } = oauthResponse.data
      setConnectionDetails({ connectionId, sourceId, destinationId })
      setCompanyName(company || realmId || 'your company')

      // Step 2: Account linked
      setCurrentStep('linking')
      await sleep(500)

      // Step 3: Trigger sync
      setCurrentStep('sync')
      
      if (connectionId) {
        const syncResponse: ApiResponse<SyncTriggerResponse> = await api.triggerSync(connectionId)
        
        if (!syncResponse.success) {
          // Don't fail the entire flow if sync trigger fails
          console.warn('Failed to trigger initial sync:', syncResponse.message)
        }
      }

      await sleep(800)

      // Step 4: Complete
      setCurrentStep('complete')
      setIsComplete(true)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(errorMessage)
      console.error('OAuth flow error:', err)
    }
  }

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

  const handleRetry = () => {
    window.location.href = '/connect'
  }

  const handleViewData = () => {
    const airbyteUrl = import.meta.env.VITE_AIRBYTE_URL || 'https://airbyte.dev.urbanski-analytics.com'
    window.open(airbyteUrl, '_blank')
  }

  return (
    <div className="container">
      <div className="header">
        <div className="quickbooks-logo">QB</div>
        <h1>
          {isComplete ? 'Successfully Connected!' : 'Connecting QuickBooks...'}
        </h1>
        {companyName && (
          <p>Setting up data sync for {companyName}</p>
        )}
      </div>

      {!error && <ProgressIndicator steps={steps} />}

      {isComplete && (
        <div className="success-message">
          <strong>🎉 All set!</strong> Your QuickBooks account has been connected and your first sync is running. 
          Data will start appearing in your analytics platform shortly.
        </div>
      )}

      {error && (
        <div className="error-message">
          <strong>Connection Failed:</strong> {error}
        </div>
      )}

      {isComplete && (
        <div className="button-group">
          <button 
            className="connect-button"
            onClick={() => window.close()}
          >
            Done
          </button>
          <button 
            className="button-secondary"
            onClick={handleViewData}
          >
            View in Airbyte
          </button>
        </div>
      )}

      {error && (
        <div className="button-group">
          <button 
            className="connect-button"
            onClick={handleRetry}
          >
            Try Again
          </button>
        </div>
      )}

      {!isComplete && !error && connectionDetails.connectionId && (
        <div className="info-section">
          <h3>Connection Details</h3>
          <p>
            <strong>Source ID:</strong> {connectionDetails.sourceId}<br />
            <strong>Connection ID:</strong> {connectionDetails.connectionId}<br />
            <strong>Destination:</strong> GCS Raw Data Bucket
          </p>
        </div>
      )}
    </div>
  )
}

export default Callback